import { FC, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material"
import { Add, Edit, DeleteOutlined, NetworkCheck } from "@mui/icons-material"
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro"
import { TooltipAction } from "../common/tooltipAction"
import { DataGrid, Modal, SecretValue } from "@/core/components"
import { useUrlListState } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import {
  useProxiesQuery,
  useDeleteProxyMutation,
  useProxyHealthCheckMutation,
} from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { ProxyForm } from "./proxyForm"

type HealthResult =
  | { ok: true; latencyMs?: number }
  | { ok: false; error: string }

const HealthCheckCell: FC<{ proxy: Userbot.Proxy }> = ({ proxy }) => {
  const [healthCheck, { isLoading }] = useProxyHealthCheckMutation()
  const [result, setResult] = useState<HealthResult | null>(null)

  const run = async () => {
    setResult(null)
    try {
      const res = await healthCheck({ id: proxy.id }).unwrap()
      if (res.ok) {
        setResult({ ok: true, latencyMs: res.latencyMs })
      } else {
        setResult({ ok: false, error: res.error ?? "неизвестная ошибка" })
      }
    } catch (e: unknown) {
      // 404 — прокси исчез между запросом списка и пробой
      const status = (e as { status?: number }).status
      const message =
        (e as { data?: { message?: string } })?.data?.message ??
        (e as { message?: string })?.message
      setResult({
        ok: false,
        error:
          status === 404 ? "прокси не найден" : (message ?? "ошибка запроса"),
      })
    }
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title="TCP-проба, не гарантирует работу с Telegram" arrow>
        <span>
          <IconButton
            size="small"
            aria-label="Проверить подключение"
            onClick={run}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <NetworkCheck fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {result && (
        <Chip
          size="small"
          color={result.ok ? "success" : "error"}
          label={
            result.ok
              ? result.latencyMs != null
                ? `OK ${result.latencyMs}ms`
                : "OK"
              : `FAIL: ${result.error}`
          }
        />
      )}
    </Box>
  )
}

export const ProxiesTable: FC = () => {
  const list = useUrlListState()
  const { data, isFetching, isError, error } = useProxiesQuery(list.listArgs, {
    pollingInterval: 60000,
  })
  const canWrite = useUserbotWriteAccess()
  const [deleteProxy, { isLoading: isDeleting }] = useDeleteProxyMutation()

  const [editing, setEditing] = useState<Userbot.Proxy | null>(null)
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<Userbot.Proxy | null>(null)

  const columns: GridColDef<Userbot.Proxy>[] = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 1, sortable: false },
      { field: "label", headerName: "Метка", flex: 2 },
      {
        field: "address",
        headerName: "Адрес",
        flex: 2,
        sortable: false,
        valueGetter: ({ row }) => `${row.host}:${row.port}`,
      },
      { field: "type", headerName: "Тип", flex: 1, sortable: false },
      {
        field: "useIpv6",
        headerName: "IPv6 выход",
        flex: 1,
        sortable: false,
        valueGetter: ({ value }) =>
          value == null ? "Авто" : value ? "IPv6" : "IPv4",
      },
      { field: "username", headerName: "Username", flex: 2, sortable: false },
      {
        field: "password",
        headerName: "Пароль",
        flex: 2,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Userbot.Proxy>) => (
          <SecretValue value={row.password} />
        ),
      },
      {
        field: "isActive",
        headerName: "Активен",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Chip size="small" color="success" label="Да" />
          ) : (
            <Chip size="small" label="Нет" />
          ),
      },
      {
        field: "healthCheck",
        headerName: "Проверка",
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<Userbot.Proxy>) => (
          <HealthCheckCell proxy={row} />
        ),
      },
    ],
    [],
  )

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button startIcon={<Add />} onClick={() => setCreating(true)}>
            Добавить прокси
          </Button>
        </Box>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}
      <Box height="80vh">
        <DataGrid<Userbot.Proxy>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={
            canWrite
              ? [
                  (r) => (
                    <TooltipAction
                      label="Редактировать"
                      icon={<Edit />}
                      onClick={() => setEditing(r.row)}
                    />
                  ),
                  (r) => (
                    <TooltipAction
                      label="Удалить"
                      icon={<DeleteOutlined />}
                      onClick={() => setToDelete(r.row)}
                    />
                  ),
                ]
              : undefined
          }
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новый прокси"
          onClose={() => setCreating(false)}
          element={<ProxyForm onDone={() => setCreating(false)} />}
        />
      )}
      {editing && (
        <Modal
          disableBackdropClose
          title="Редактирование прокси"
          onClose={() => setEditing(null)}
          element={
            <ProxyForm proxy={editing} onDone={() => setEditing(null)} />
          }
        />
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Удалить прокси?"
        description={toDelete ? `${toDelete.host}:${toDelete.port}` : ""}
        loading={isDeleting}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteProxy({ id: toDelete.id })
            setToDelete(null)
          }
        }}
      />
    </>
  )
}
